import React from "react";
import { AppConfigContext } from "./contexts/app/appConfig";
import type { AppConfig } from "./contexts/app/appConfig";
import { zekayTheme, GlobalStyles } from "./styles/globalStyles";
import CustomersPage from "./components/customers/customer/CustomersPage";
import LoginPage from "./components/auth/LoginPage";
import ResetPasswordPage from "./components/auth/ResetPasswordPage";
import ResetRequestPage from "./components/auth/ResetRequestPage";
import CreateInvitedUserPage from "./components/auth/CreateInvitedUserPage";
import MenuPage from "./components/menu/MenuPage";
import Home from "./components/Home";
import ExcursionsPage from "./components/excursions/planning/ExcursionsPage";
import ExcursionParamsPage from "./components/excursions/template/ExcursionParamsPage";
import { ExcursionsDataProvider } from "./contexts/app/excursionsData";
import { UsersModuleDataProvider } from "./contexts/app/usersModuleData";
import { HRDataProvider } from "./contexts/app/hrData";
import PlanningPage from "./components/planning/gerenalPlanning/PlanningPage";
import ExcursionBookingPage from "./components/planning/booking/booking/ExcursionBookingPage";
import LeaveManagementComponent from "./components/hr/LeaveManagementComponent";
import LeaveValidationPage from "./components/hr/LeaveValidationPage";
import LeaveBalanceComponent from "./components/hr/LeaveBalanceComponent";
import LeaveTypesPage from "./components/hr/LeaveTypesPage";
import { AlertProvider } from "./contexts/app/notificationToast";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, FetchHeadersProvider, RolesProvider } from "./contexts/api";
import { FetchRateLimiterProvider } from "./contexts/api/rateLimiter";
import type { BackendPermissionKey } from "./contexts/app/permissions";
import { PermissionsProvider, usePermissions } from "./contexts/app/permissions";
import { useAuth } from "./contexts/api";
import useAppSettings from "./contexts/app/appSettings";
import { ConfirmationModalProvider } from "./contexts/app/confirmation";
import { CssBaseline } from "@mui/joy";
import { CssVarsProvider } from "@mui/joy/styles";
import {
  SAssignment,
  SCalendarMonth,
  SCategory,
  SCreditCard,
  SGroups,
  SInventory,
  SMailOutline,
  SSchool,
  SSettings,
  SProduct,
  SInvoice,
  SStats,
  STemplates,
  SUsers,
  SVerifiedUser,
  SWallet,
  SPerson,
  SRental,
  SEvent,
  SSmartphone,
  SSms,
  SInfo,
} from "./styles/StyledMUI";
import EventsPage from "./components/events/EventsPage";
import CardValidationList from "./components/trainings/cardValidation/cardValidationList";
import DashboardPage from "./components/common/DashboardPage";
import TabsPage from "./components/common/TabsPage";
import GoogleGroupsPage from "./components/communication/googleGroups/GoogleGroupsPage";
import CommunicationSettingsPage from "./components/communication/settings/CommunicationSettingsPage";
import CentreInfoPage from "./components/centre/CentreInfoPage";
import MailTemplatesPage from "./components/communication/mailsEditor/MailTemplatesPage";
import SMSTemplatesPage from "./components/communication/smsEditor/SMSTemplatesPage";
import UserInvitationMailPage from "./components/users/invitationMail/UserInvitationMailPage";
import MobileDevicesPage from "./components/centre/mobileDevices/MobileDevicesPage";
import RolesPage from "./components/users/roles/RolesPage";
import QualificationsPage from "./components/customers/qualifications/QualificationsPage";
import TrainingTemplateEditor from "./components/trainings/templates/display/TrainingTemplatesEditor";
import CourseTemplateEditor from "./components/trainings/templates/display/CourseTemplateEditor";
import TheoryTestTemplateEditor from "./components/trainings/templates/display/TheoryTestTemplatesEditor";
import TrainingsList from "./components/trainings/trainings/display/TrainingsList";
import StatsPage from "./components/stats/StatsPage";
import PrestationsPage from "./components/billing/prestations/PrestationsPage";
import ProductsPage from "./components/billing/products/ProductsPage";
import RentalProductsPage from "./components/billing/location/RentalProductsPage";
import RentalPage from "./components/rental/RentalPage";
import UserListPage from "./components/users/userList/UserListPage";
import EquipmentModelsComponent from "./components/equipments/EquipmentModelsComponent";
import EquipmentsComponent from "./components/equipments/EquipmentsComponent";
import CompanySelectModal from "./components/auth/CompanySelectModal";
import { BellAlertDisplayProvider } from "./contexts/app/bellAlertsContext";
import { DomainsProvider } from "./contexts/api";
import { AppSettingsProvider } from "./contexts/app/appSettings";
import {
  BuildAlertRouteConfigFromMenuTiles,
  GetMenuTilesCoreDefinition,
  GetModulePathAccessPermission,
  GetSidebarItemsRightsConfig,
} from "./components/menu/config/menuConfig";
import type { SidebarItemRightsConfig } from "./components/menu/config/menuConfig";

export default function App() {
  const [appConfig, setAppConfig] = React.useState<AppConfig>(undefined!);
  const [configError, setConfigError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const env = (import.meta.env.VITE_APP_ENV || "").toLowerCase();
    document.title = env === "dev" ? "Zekay (Dev)" : "Zekay";

    fetch(`${window.location.origin}/config.json`)
      .then((response) => {
        if (!response.ok) throw new Error(`config.json HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (!data?.zekayAPI) throw new Error("config.json: champ zekayAPI manquant");
        setAppConfig(data);
      })
      .catch((err) => {
        console.error("[Zekay] Impossible de charger config.json", err);
        setConfigError(err?.message ?? String(err));
      });
  }, []);

  if (configError) {
    return (
      <div
        style={{
          padding: 32,
          fontFamily: "sans-serif",
          color: "#fff",
          background: "#1a1a2e",
          minHeight: "100vh",
        }}
      >
        <h2>Zekay – Erreur de chargement</h2>
        <p>Impossible de charger la configuration de l'application.</p>
        <pre style={{ color: "#ff6b6b" }}>{configError}</pre>
        <button
          onClick={() => window.location.reload()}
          style={{ marginTop: 16, padding: "8px 16px", cursor: "pointer" }}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <AppConfigContext.Provider value={appConfig}>
      <CssVarsProvider theme={zekayTheme}>
        <GlobalStyles />
        <CssBaseline />
        {appConfig !== undefined ? (
          <AlertProvider>
            <AuthProvider>
              <FetchHeadersProvider>
                <FetchRateLimiterProvider>
                  <AppWithPermissions />
                </FetchRateLimiterProvider>
              </FetchHeadersProvider>
            </AuthProvider>
          </AlertProvider>
        ) : null}
      </CssVarsProvider>
    </AppConfigContext.Provider>
  );
}

function AppWithPermissions() {
  const { user } = useAuth();
  const userPermissions = React.useMemo<BackendPermissionKey[]>(() => {
    const perms = user?.Role?.permission_list || [];
    return perms as unknown as BackendPermissionKey[];
  }, [user]);

  return (
    <PermissionsProvider userPermissions={userPermissions}>
      <ConfirmationModalProvider>
        <RolesProvider>
          <CompanySelectModal />
          <BellAlertDisplayProvider
            config={BuildAlertRouteConfigFromMenuTiles(GetMenuTilesCoreDefinition())}
          >
            <AppRoutes />
          </BellAlertDisplayProvider>
        </RolesProvider>
      </ConfirmationModalProvider>
    </PermissionsProvider>
  );
}

function AppRoutes() {
  return (
    <DomainsProvider>
      <AppSettingsProvider>
        <AppRoutesInner />
      </AppSettingsProvider>
    </DomainsProvider>
  );
}

function ModuleAccessRoute({ segment, children }: { segment: string; children: React.ReactNode }) {
  const { hasPermission } = usePermissions();
  const { config } = useAppSettings();
  const isDesync = config?.CustomersSync === false;
  const required = GetModulePathAccessPermission()[segment];
  if (required != null && !hasPermission(required)) {
    return <Navigate to="/" replace />;
  }
  if (segment === "billing" && !isDesync) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function AppRoutesInner() {
  const { hasAnyPermission, hasPermission } = usePermissions();
  const { config } = useAppSettings();
  const isDesync = config?.CustomersSync === false;

  const sidebarRights = React.useMemo(() => GetSidebarItemsRightsConfig(), []);
  const allow = React.useCallback(
    (item: SidebarItemRightsConfig) => {
      const hasAll = !item.allOf?.length || item.allOf.every((p) => hasPermission(p));
      const hasAny = !item.anyOf?.length || hasAnyPermission(item.anyOf);
      if (!item.allOf?.length && !item.anyOf?.length) return false;
      return hasAll && hasAny;
    },
    [hasPermission, hasAnyPermission],
  );

  const sidebarUI = React.useMemo(
    () =>
      ({
        "planning.calendar": { icon: <SCalendarMonth />, component: <PlanningPage /> },
        "planning.bookings": { icon: <SAssignment />, component: <ExcursionBookingPage /> },
        "hr.leave": { icon: <SPerson />, component: <LeaveManagementComponent /> },
        "hr.validation": { icon: <SVerifiedUser />, component: <LeaveValidationPage /> },
        "hr.balances": { icon: <SWallet />, component: <LeaveBalanceComponent /> },
        "hr.types": { icon: <SCategory />, component: <LeaveTypesPage /> },
        "trainings.cards": { icon: <SCreditCard />, component: <CardValidationList /> },
        "trainings.list": {
          icon: <SSchool />,
          component: <TrainingsList />,
        },
        "trainings.templates": {
          icon: <STemplates />,
          component: (
            <TabsPage
              title="Modèles"
              tabs={[
                { label: "Formations", component: <TrainingTemplateEditor /> },
                { label: "Suivis", component: <CourseTemplateEditor /> },
                { label: "QCM", component: <TheoryTestTemplateEditor /> },
              ]}
            />
          ),
        },
        "customers.list": { icon: <SPerson />, component: <CustomersPage /> },
        "customers.qualifications": { icon: <SCategory />, component: <QualificationsPage /> },
        "inventory.equipments": { icon: <SInventory />, component: <EquipmentsComponent /> },
        "inventory.models": { icon: <STemplates />, component: <EquipmentModelsComponent /> },
        "stats.overview": { icon: <SStats />, component: <StatsPage /> },
        "billing.prestations": { icon: <SInvoice />, component: <PrestationsPage /> },
        "billing.products": { icon: <SProduct />, component: <ProductsPage /> },
        "billing.rental": { icon: <SRental />, component: <RentalProductsPage /> },
        "events.list": { icon: <SEvent />, component: <EventsPage /> },
        "rental.list": { icon: <SRental />, component: <RentalPage /> },
        "excursions.planning": {
          icon: <SCalendarMonth />,
          component: <ExcursionsPage />,
        },
        "excursions.params": {
          icon: <SSettings />,
          component: <ExcursionParamsPage />,
        },
        "users.list": { icon: <SUsers />, component: <UserListPage /> },
        "users.roles": { icon: <SVerifiedUser />, component: <RolesPage /> },
        "users.settings": { icon: <SSettings />, component: <UserInvitationMailPage /> },
        "communication.mails": { icon: <SMailOutline />, component: <MailTemplatesPage /> },
        "communication.sms": { icon: <SSms />, component: <SMSTemplatesPage /> },
        "communication.googleGroups": { icon: <SGroups />, component: <GoogleGroupsPage /> },
        "communication.settings": { icon: <SSettings />, component: <CommunicationSettingsPage /> },
        "centre.info": { icon: <SInfo />, component: <CentreInfoPage /> },
        "centre.mobileDevices": {
          icon: <SSmartphone />,
          component: <MobileDevicesPage />,
        },
      }) as Record<string, { icon: JSX.Element; component: JSX.Element }>,
    [],
  );

  const menuTilesByPath = React.useMemo(() => {
    const tiles = GetMenuTilesCoreDefinition();
    const map: Record<string, { enabled: boolean }> = {};
    tiles.forEach((t) => {
      const key = (t.path || "").replace(/^\//, "") || "index";
      map[key] = { enabled: t.enabled };
    });
    return map;
  }, []);

  const buildSidebar = React.useCallback(
    (sectionKey: string) => {
      const sectionTile = menuTilesByPath[sectionKey];
      if (sectionTile && !sectionTile.enabled) return [];
      if (sectionKey === "billing" && !isDesync) return [];
      const raw = sidebarRights[sectionKey];
      let items = Array.isArray(raw) ? raw.filter(allow) : [];
      if (sectionKey === "customers" && !isDesync) {
        items = items.filter((item) => !item.hideWhenDesync);
      }
      return items.map((item) => ({
        label: item.label,
        icon: sidebarUI[item.id]?.icon ?? <span />,
        component: sidebarUI[item.id]?.component ?? <div>Non configuré</div>,
      }));
    },
    [sidebarRights, allow, sidebarUI, isDesync, menuTilesByPath],
  );

  const sidebarPlanning = React.useMemo(() => buildSidebar("planning"), [buildSidebar]);
  const sidebarHR = React.useMemo(() => buildSidebar("hr"), [buildSidebar]);
  const gatedExcursions = React.useMemo(() => buildSidebar("excursions"), [buildSidebar]);
  const gatedTrainings = React.useMemo(() => buildSidebar("trainings"), [buildSidebar]);
  const gatedCustomers = React.useMemo(() => buildSidebar("customers"), [buildSidebar]);
  const gatedInventory = React.useMemo(() => buildSidebar("inventory"), [buildSidebar]);
  const gatedStats = React.useMemo(() => buildSidebar("stats"), [buildSidebar]);
  const gatedUsers = React.useMemo(() => buildSidebar("users"), [buildSidebar]);
  const gatedBilling = React.useMemo(() => buildSidebar("billing"), [buildSidebar]);
  const gatedRental = React.useMemo(() => buildSidebar("rental"), [buildSidebar]);
  const gatedEvents = React.useMemo(() => buildSidebar("events"), [buildSidebar]);
  const gatedCommunication = React.useMemo(() => buildSidebar("communication"), [buildSidebar]);
  const gatedCentre = React.useMemo(() => buildSidebar("centre"), [buildSidebar]);

  return (
    <Routes>
      <Route path="/reset" element={<ResetPasswordPage />} />
      <Route path="/request" element={<ResetRequestPage />} />
      <Route path="/create" element={<CreateInvitedUserPage />} />
      <Route path="/" element={<Home />}>
        <Route index element={<MenuPage />} />
        <Route
          path="customers"
          element={
            <ModuleAccessRoute segment="customers">
              <DashboardPage sidebarConfig={gatedCustomers} brandBackdropModule="customers" />
            </ModuleAccessRoute>
          }
        />
        <Route
          path="events"
          element={
            <ModuleAccessRoute segment="events">
              <DashboardPage sidebarConfig={gatedEvents} brandBackdropModule="events" />
            </ModuleAccessRoute>
          }
        />
        <Route
          path="excursions"
          element={
            <ModuleAccessRoute segment="excursions">
              <ExcursionsDataProvider>
                <DashboardPage sidebarConfig={gatedExcursions} brandBackdropModule="excursions" />
              </ExcursionsDataProvider>
            </ModuleAccessRoute>
          }
        />
        <Route
          path="planning"
          element={
            <ModuleAccessRoute segment="planning">
              <HRDataProvider>
                <DashboardPage sidebarConfig={sidebarPlanning} brandBackdropModule="planning" />
              </HRDataProvider>
            </ModuleAccessRoute>
          }
        />
        <Route
          path="hr"
          element={
            <ModuleAccessRoute segment="hr">
              <HRDataProvider>
                <DashboardPage sidebarConfig={sidebarHR} brandBackdropModule="hr" />
              </HRDataProvider>
            </ModuleAccessRoute>
          }
        />
        <Route
          path="trainings"
          element={
            <ModuleAccessRoute segment="trainings">
              <DashboardPage sidebarConfig={gatedTrainings} brandBackdropModule="trainings" />
            </ModuleAccessRoute>
          }
        />
        <Route
          path="inventory"
          element={
            <ModuleAccessRoute segment="inventory">
              <DashboardPage sidebarConfig={gatedInventory} brandBackdropModule="inventory" />
            </ModuleAccessRoute>
          }
        />
        <Route
          path="billing"
          element={
            <ModuleAccessRoute segment="billing">
              <DashboardPage sidebarConfig={gatedBilling} brandBackdropModule="billing" />
            </ModuleAccessRoute>
          }
        />
        <Route
          path="stats"
          element={
            <ModuleAccessRoute segment="stats">
              <DashboardPage sidebarConfig={gatedStats} brandBackdropModule="stats" />
            </ModuleAccessRoute>
          }
        />
        <Route
          path="rental"
          element={
            <ModuleAccessRoute segment="rental">
              <DashboardPage sidebarConfig={gatedRental} brandBackdropModule="rental" />
            </ModuleAccessRoute>
          }
        />
        <Route
          path="users"
          element={
            <ModuleAccessRoute segment="users">
              <UsersModuleDataProvider>
                <DashboardPage sidebarConfig={gatedUsers} brandBackdropModule="users" />
              </UsersModuleDataProvider>
            </ModuleAccessRoute>
          }
        />
        <Route
          path="communication"
          element={
            <ModuleAccessRoute segment="communication">
              <DashboardPage
                sidebarConfig={gatedCommunication}
                brandBackdropModule="communication"
              />
            </ModuleAccessRoute>
          }
        />
        <Route
          path="centre"
          element={
            <ModuleAccessRoute segment="centre">
              <DashboardPage sidebarConfig={gatedCentre} brandBackdropModule="centre" />
            </ModuleAccessRoute>
          }
        />
        <Route path="settings" element={<Navigate to="/centre" replace />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="*" element={<LoginPage />} />
      </Route>
    </Routes>
  );
}
